import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Info,
  MessageSquareText,
  TrendingUp,
  Wand2,
} from "lucide-react";

import { createReport } from "../api/perception";
import { useAuth } from "../contexts/AuthContext";
import PerceptionLoaderDialog from "./PerceptionLoaderDialog";

const EXAMPLE_ATTRIBUTES = [
  { label: "Location", score: 8.4 },
  { label: "Value for Money", score: 4.6 },
  { label: "Amenities", score: 3.1 },
  { label: "Family-Friendliness", score: 7.2 },
];

const PerceptionEngine = () => {
  const navigate = useNavigate();
  const { user, businessProfile } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [reportName, setReportName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  const isProfileComplete = businessProfile?.is_complete;

  const openAuth = () => navigate("?auth=login");
  const openBusinessProfile = () => window.dispatchEvent(new CustomEvent("openBusinessProfile"));

  const handleGenerate = () => {
    if (!user) {
      openAuth();
      return;
    }
    if (!isProfileComplete) {
      openBusinessProfile();
      return;
    }
    setConfirming(true);
  };

  const handleRunScan = async () => {
    setError(null);
    setScanning(true);
    try {
      const res = await createReport(reportName.trim() || undefined);
      window.dispatchEvent(new CustomEvent("perceptionReportsUpdated"));
      navigate(`/perception/report/${res.data.report_id}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (detail === "profile_required") {
        setError("Complete your business profile to generate a report.");
        openBusinessProfile();
      } else if (status === 503) {
        setError("No AI providers are available right now. Please try again later.");
      } else {
        setError((typeof detail === "string" ? detail : null) || "Failed to generate report.");
      }
      setScanning(false);
    }
  };

  const renderScanAction = () => {
    if (!user) {
      return (
        <button
          onClick={openAuth}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          Log In to Run Scan
          <ArrowRight className="w-4 h-4" />
        </button>
      );
    }

    if (!isProfileComplete) {
      return (
        <button
          onClick={openBusinessProfile}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          Complete Business Profile First
          <ArrowRight className="w-4 h-4" />
        </button>
      );
    }

    if (!confirming) {
      return (
        <button
          onClick={handleGenerate}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          <Brain className="w-4 h-4" />
          Generate AI Perception Report
        </button>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <button
            onClick={() => setConfirming(false)}
            className="flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-primary transition-colors mb-3"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <h3 className="font-headline text-base font-bold mb-1">Confirm what we&apos;ll score</h3>
          <p className="text-xs text-on-surface-variant mb-3">
            We&apos;ll ask ChatGPT, Gemini &amp; Claude to describe this business and score how they
            perceive it.
          </p>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 p-4 space-y-2">
            <div className="text-sm font-semibold text-on-surface">
              {businessProfile?.business_name}
            </div>
            <div className="text-xs text-on-surface-variant">
              {[businessProfile?.city, businessProfile?.country].filter(Boolean).join(", ")}
              {businessProfile?.price_tier ? ` · ${businessProfile.price_tier}` : ""}
            </div>
            {businessProfile?.amenities?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {businessProfile.amenities.map((a) => (
                  <span
                    key={a}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant"
                  >
                    {a}
                  </span>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder="Report name (optional)"
            className="w-full mt-3 px-3 py-2 rounded-xl bg-surface-container-lowest border border-outline-variant/30 text-sm text-on-surface placeholder:text-on-surface-variant focus:border-primary focus:outline-none"
          />
        </div>

        {error && <p className="text-error text-sm font-medium">{error}</p>}

        <button
          onClick={handleRunScan}
          disabled={scanning}
          className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
        >
          <Brain className="w-4 h-4" />
          Run AI Perception Scan
        </button>
      </div>
    );
  };

  return (
    <>
      {scanning && <PerceptionLoaderDialog />}
      <main className="pt-10 pb-24 px-6 md:px-12 max-w-8xl mx-auto">
        <section className="mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-label text-[10px] font-extrabold tracking-[0.15em] mb-6 uppercase">
                AI Perception Scoring
              </span>

              <h1 className="font-headline text-5xl md:text-7xl font-extrabold tracking-tight text-on-surface mb-8 leading-[1.05]">
                Not Just If You Appear — How You&apos;re Described
              </h1>

              <p className="font-body text-xl text-on-surface-variant max-w-2xl mb-6 leading-relaxed">
                We ask ChatGPT, Gemini, and Claude to describe your business, then score every
                attribute — location, value, amenities, and more — and flag exactly which content
                fixes close the gap.
              </p>

              <div className="flex flex-wrap gap-6 mb-12 text-sm text-on-surface-variant">
                <div className="flex items-center gap-2">
                  <MessageSquareText className="w-4 h-4 text-primary" />
                  <span>Strengths, weaknesses &amp; blind spots</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-emerald-400" />
                  <span>Fixes mapped to every gap</span>
                </div>
              </div>

              <div className="bg-surface-container-low rounded-2xl p-6 border border-outline/10">
                <div className="flex items-start gap-2 mb-4">
                  <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-xs text-on-surface-variant leading-relaxed">
                    Perception scans use your business profile - no separate setup required. Make
                    sure your Business Profile is up to date and reflects your business.
                  </p>
                </div>
                {renderScanAction()}
                {user && !confirming && (
                  <button
                    onClick={() => navigate("/perception/trends")}
                    className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
                  >
                    <TrendingUp className="w-4 h-4" />
                    See your perception trends
                  </button>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4 text-outline" />
                <span className="text-xs text-on-surface-variant font-medium">
                  Example: attribute scores (0–10)
                </span>
              </div>
              <div className="bg-surface-container-lowest rounded-2xl border border-surface-container-high p-6 space-y-3">
                {EXAMPLE_ATTRIBUTES.map((a) => (
                  <div key={a.label} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{a.label}</div>
                      <div className="mt-1.5 h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            a.score >= 6
                              ? "bg-emerald-500"
                              : a.score >= 5
                                ? "bg-yellow-500"
                                : "bg-orange-500"
                          }`}
                          style={{ width: `${a.score * 10}%` }}
                        />
                      </div>
                    </div>
                    <div
                      className={`text-lg font-extrabold font-headline shrink-0 ${
                        a.score >= 6
                          ? "text-emerald-400"
                          : a.score >= 5
                            ? "text-yellow-400"
                            : "text-orange-400"
                      }`}
                    >
                      {a.score.toFixed(1)}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-on-surface-variant/60 text-center mt-2">
                Example data — your report will show real results
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-headline text-3xl font-bold text-on-surface mb-12 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageSquareText className="w-6 h-6 text-primary" />,
                title: "We Ask Three AIs to Describe You",
                desc: "Open-ended prompts across ChatGPT, Gemini, and Claude elicit how each engine actually talks about your business.",
              },
              {
                icon: <Brain className="w-6 h-6 text-primary" />,
                title: "Score Every Attribute",
                desc: "Location, service, value, amenities, and more are scored 0-10 from sentiment and evidence in each response.",
              },
              {
                icon: <Wand2 className="w-6 h-6 text-primary" />,
                title: "Get Mapped Fixes",
                desc: "Every weakness and blind spot links to a concrete content fix — and you can track the score change over time.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-surface-container-low rounded-2xl p-8 border border-outline/10"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-headline text-xl font-bold text-on-surface mb-2">
                  {item.title}
                </h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default PerceptionEngine;
